import { useMemo, useContext, useEffect, useState } from "react";
import { Box, Hidden, CircularProgress, Popover, Tooltip } from '@mui/material'

import LanguageContext from "../../../contexts/LanguageContext"

import chatInfoIcon from "../../../images/chatInfoIcon.svg"

/*const DEFAULT_DATA = [
  { label: "0/4.9",    moyStartScore: 8,  moyEndScore: 25, progression: 17 },
  { label: "5/14.9",  moyStartScore: 20, moyEndScore: 30, progression: 10 },
  { label: "15/49.9", moyStartScore: 40, moyEndScore: 55, progression: 15 },
  { label: "50/84.9",  moyStartScore: 45, moyEndScore: 60, progression: 15 },
  { label: "85/94.9",  moyStartScore: 55, moyEndScore: 71, progression: 16 },
  { label: "95/100",   moyStartScore: 62, moyEndScore: 75, progression: 13 },
];*/


const widths = [10, 17, 23, 23, 17, 10]

const SCALE_STEPS = [25, 50, 75, 100];
const CHART_H = 127;
const BADGE_H = 30;
/* P1 (03/09) : le libelle vertical de l'axe Y ajoute une colonne a gauche du
   graphe. Tout ce qui doit rester cale sur la zone de tracé (graduations
   horizontales, regle de positionnement) utilise DECALAGE_TRACE, sinon
   l'ajout du libelle desaligne l'axe des abscisses. */
const LARGEUR_AXE_Y = 34;      // libelle vertical + pastille d'aide
const LARGEUR_GRADUATIONS = 30; // colonne des « 25% / 50% / ... »
const ECART_TRACE = 8;          // gap du conteneur flex
const DECALAGE_TRACE = LARGEUR_AXE_Y + LARGEUR_GRADUATIONS + ECART_TRACE;

/* D10 — regle de positionnement (03/09).
 *
 * Le percentile d'une tranche est la part des apprenants situes EN DESSOUS
 * d'elle : c'est le meme calcul que les bulles au survol des barres, on ne
 * reinvente donc pas une definition. Il se lit sur une echelle 0 a 100 fixe,
 * jamais sur l'axe des scores.
 *
 * Trois zones, celles retenues par Hicham : 0-25 les plus en difficulte,
 * 25-75 dans la moyenne, 75-100 les plus avances. */
const ZONES = [
  { de: 0,  a: 25,  cle: "positionnementZoneBas",     fond: "rgba(154,154,154,0.16)" },
  { de: 25, a: 75,  cle: "positionnementZoneMoyenne", fond: "rgba(154,154,154,0.08)" },
  { de: 75, a: 100, cle: "positionnementZoneHaut",    fond: "rgba(216,90,48,0.12)" },
]

export const percentileDeLaTranche = (data, i) => {
  if (!Array.isArray(data) || data.length === 0) return 0
  const total = data.reduce((acc, x) => acc + (x?.nbrStudents || 0), 0)
  if (total <= 0) return 0
  const dessous = data.slice(0, i).reduce((acc, x) => acc + (x?.nbrStudents || 0), 0)
  return Math.max(1, Math.min(100, Math.round((dessous / total) * 100)))
}

/* P1 (fiche V3, 03/09) : une pastille « ? » qui explique COMMENT lire un axe.
   Meme esprit que l'explication des percentiles demandee par Nawal : le client
   n'est pas cense deviner ce que porte l'axe. */
const PastilleAide = ({ texte }) => (
  <Tooltip arrow placement="top" title={texte || ""}>
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: "15px", height: "15px", minWidth: "15px", borderRadius: "50%",
      border: "1px solid rgba(10,10,11,0.45)", color: "rgba(10,10,11,0.65)",
      fontSize: "10px", lineHeight: 1, cursor: "help", userSelect: "none",
      marginLeft: "6px", flexShrink: 0,
    }}>?</Box>
  </Tooltip>
)

const ReglePositionnement = ({ data }) => {
  const { actLanguage, languageValues } = useContext(LanguageContext)
  const textes = languageValues?.adminDashboard?.mappingPage?.graph

  // une tranche vide n'a personne a positionner
  const tranches = (Array.isArray(data) ? data : [])
    .map((d, i) => ({ label: d?.label, nbr: d?.nbrStudents || 0, percentile: percentileDeLaTranche(data, i) }))
    .filter((t) => t.nbr > 0)

  if (tranches.length === 0) return null

  return (
    <Box sx={{ marginLeft: DECALAGE_TRACE+"px", marginTop: "22px", paddingBottom: "4px" }}>
      <Box sx={{ fontSize: "12px", color: "#888", marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{ fontSize: "10px", color: "#0A0A0B" }}>{'◆'}</span>
        {textes?.positionnement?.[actLanguage]}
      </Box>
      {/* Pas d'`overflow: hidden` ici : les curseurs depassent volontairement en
          haut de la barre, et un rognage les coupait en deux. Les coins sont
          arrondis sur les zones d'extremite a la place. */}
      <Box sx={{ position: "relative", height: "10px", display: "flex" }}>
        {ZONES.map((z, i) => (
          <Box key={z.cle} sx={{
            width: (z.a - z.de) + "%", background: z.fond,
            borderTopLeftRadius: i === 0 ? "5px" : 0, borderBottomLeftRadius: i === 0 ? "5px" : 0,
            borderTopRightRadius: i === ZONES.length - 1 ? "5px" : 0,
            borderBottomRightRadius: i === ZONES.length - 1 ? "5px" : 0,
          }} />
        ))}
        {tranches.map((t, i) => (
          <Tooltip
            key={i}
            arrow
            placement="top"
            title={(textes?.positionnementBulle?.[actLanguage] || "").split("{percentile}").join(String(t.percentile))}
          >
            <Box sx={{
              position: "absolute",
              left: t.percentile + "%",
              top: "-2px",
              transform: "translateX(-50%) rotate(45deg)",
              width: "11px", height: "11px",
              background: "#0A0A0B",
              cursor: "pointer",
              zIndex: 1,
            }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: "flex", marginTop: "6px", fontSize: "11px", color: "#888" }}>
        {ZONES.map((z) => (
          <Box key={z.cle} sx={{ width: (z.a - z.de) + "%", textAlign: "center", paddingX: "2px" }}>
            {textes?.[z.cle]?.[actLanguage]}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const ProgressionChart = ({ data, seuils }) => {

  const { actLanguage, languageValues } = useContext(LanguageContext)

  let viewExpectedLevel = false
  let expectedLevel = 0
  if (seuils && seuils?.find === true) {
    viewExpectedLevel = true
    expectedLevel = seuils?.after || 0
  }

  let maxValStart = useMemo(() => Math.max(...data.map((d) => d.moyStartScore)), [data]);
  let maxValEnd = useMemo(() => Math.max(...data.map((d) => d.moyEndScore)), [data]);
  let maxVal = Math.max(maxValStart, maxValEnd, expectedLevel)
  let MAX_VAL = useMemo(
    () => SCALE_STEPS.find((s) => s >= maxVal) ?? 100,
    [maxVal]
  );

  const ticks = useMemo(() => {
    const t = SCALE_STEPS.filter((s) => s <= MAX_VAL);
    if (!t.includes(0)) t.unshift(0);
    return t;
  }, [MAX_VAL]);

  let maxTick = ticks[ticks?.length - 1]


  let [beforePopover, setBeforePopover] = useState(false)
  let [afterPopover, setAfterPopover] = useState(false)
  let [canViewTooltips, setCanViewTooltips] = useState(false)
  // E4 review 02/09 : les barres montent en douceur a l'ouverture
  const [anime, setAnime] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setAnime(true), 80)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    let totalNbrStudents = 0
    data?.forEach((d) => {
      totalNbrStudents += d?.nbrStudents || 0
    })
    if (totalNbrStudents === 0) {
      setCanViewTooltips(false)
    } else {
      //on va regarder l'index de la barre qu'on veut pour sertir d'anchor aux tooltips, on préfère le plus à droite si possible
      for (let i=data?.length-1; i>=0; i--) {
        if (data[i]?.nbrStudents > 0) {
          setCanViewTooltips(i)
          break;
        }
      }
    }
  }, [data])


  return (
    <>
      <Box sx={{maxWidth:"1000px", userSelect:"none"}}>
        <Box sx={{display:"flex", justifyContent:"space-between", gap:"10px"}}>
          <Box sx={{fontSize:["14px", "16px"], color:"text.subTitle"}}>
            {languageValues?.adminDashboard?.mappingPage?.graph?.title[actLanguage]}
          </Box>
          <Hidden smDown>
            <Box sx={{display:"flex", gap:"10px", justifyContent:"flex-end", alignItems:"center"}}>
              <Box sx={{fontSize:"16px", color:"text.subTitle"}}>
                {languageValues?.adminDashboard?.mappingPage?.graph?.legends[actLanguage]}
              </Box>
              <Box
                onClick={() => {
                  if (canViewTooltips !== false) {
                    setBeforePopover(true)
                    document?.getElementById("anchorTooltipBefore_"+canViewTooltips)?.scrollIntoView({container:"nearest", inline:"center"})
                  }
                }}
                sx={{
                  padding: "7px 10px",
                  background: "#F3CCBD",
                  borderRadius: "12px",
                  fontSize:"12px",
                  opacity:canViewTooltips !== false ? 1 : 0.5,
                  cursor:canViewTooltips !== false && "pointer",
                }}
              >
                {languageValues?.adminDashboard?.mappingPage?.graph?.before[actLanguage]}
              </Box>
              <Box
                onClick={() => {
                  if (canViewTooltips !== false) {
                    setAfterPopover(true)
                    document?.getElementById("anchorTooltipAfter_"+canViewTooltips)?.scrollIntoView({container:"nearest", inline:"center"})
                  }
                }}
                sx={{
                  padding: "7px 10px",
                  background: "#E26438",
                  borderRadius: "12px",
                  fontSize:"12px",
                  color:"white",
                  opacity:canViewTooltips !== false ? 1 : 0.5,
                  cursor:canViewTooltips !== false && "pointer",
                }}
              >
                {languageValues?.adminDashboard?.mappingPage?.graph?.after[actLanguage]}
              </Box>
              {/* D10 review 02/09 : troisieme legende, conforme Figma */}
              <Box sx={{
                padding: "7px 10px",
                background: "white",
                border: "1.5px solid #0A0A0B",
                borderRadius: "12px",
                fontSize:"12px",
                color:"#0A0A0B",
                display:"flex",
                alignItems:"center",
                gap:"6px",
              }}>
                <span style={{fontSize:"10px"}}>{'\u25C6'}</span>
                {languageValues?.adminDashboard?.mappingPage?.graph?.positionnement?.[actLanguage]}
              </Box>
            </Box>
          </Hidden>
        </Box>
        <Box sx={{fontSize:["22px", "26px"], marginTop:["5px", "5px"]}}>
          {languageValues?.adminDashboard?.mappingPage?.graph?.subTitle[actLanguage]}
        </Box>
        <Hidden smUp>
          <Box sx={{marginTop:"5px", display:"flex", gap:"10px", justifyContent:"flex-start", alignItems:"center"}}>
            <Box sx={{fontSize:"13px", color:"text.subTitle"}}>
              {languageValues?.adminDashboard?.mappingPage?.graph?.legends[actLanguage]}
            </Box>
            <Box
              onClick={() => {
                if (canViewTooltips !== false) {
                  setBeforePopover(true)
                  document?.getElementById("anchorTooltipBefore_"+canViewTooltips)?.scrollIntoView({container:"nearest", inline:"center"})
                }
              }}
              sx={{
                padding: "6px 8px",
                background: "#F3CCBD",
                borderRadius: "12px",
                fontSize:"11px",
                opacity:canViewTooltips !== false ? 1 : 0.5,
                cursor:canViewTooltips !== false && "pointer",
              }}>
              {languageValues?.adminDashboard?.mappingPage?.graph?.before[actLanguage]}
            </Box>
            <Box
              onClick={() => {
                if (canViewTooltips !== false) {
                  setAfterPopover(true)
                  document?.getElementById("anchorTooltipAfter_"+canViewTooltips)?.scrollIntoView({container:"nearest", inline:"center"})
                }
              }}
              sx={{
                padding: "6px 8px",
                background: "#E26438",
                borderRadius: "12px",
                fontSize:"11px",
                color:"white",
                opacity:canViewTooltips !== false ? 1 : 0.5,
                cursor:canViewTooltips !== false && "pointer",
              }}
            >
              {languageValues?.adminDashboard?.mappingPage?.graph?.after[actLanguage]}
            </Box>
          </Box>
        </Hidden>
        <Box sx={{marginTop:["10px", "20px"], overflowX:"auto", overflowY:"hidden", scrollbarColor: "#FAF8F6 transparent",}}>

          {data?.length > 0 ? (
              <Box sx={{ paddingTop: BADGE_H+"px", width:"100%", minWidth:"600px", minHeight:"177px", boxSizing: "border-box",userSelect:"none" }}>
                {/* Chart area */}
                <Box sx={{ display: "flex", gap: "8px" }}>
                  {/* P1 : l'axe vertical est nomme, et une bulle dit comment le lire */}
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, width: LARGEUR_AXE_Y+"px", height: CHART_H+"px" }}>
                    <Box sx={{
                      writingMode: "vertical-rl", transform: "rotate(180deg)",
                      fontSize: "11px", color: "#888", whiteSpace: "nowrap",
                      display: "flex", alignItems: "center", gap: "4px",
                      marginRight: "2px",
                    }}>
                      {languageValues?.adminDashboard?.mappingPage?.graph?.axeY?.[actLanguage]}
                    </Box>
                    <Box sx={{ marginRight: "2px" }}>
                      <PastilleAide texte={languageValues?.adminDashboard?.mappingPage?.graph?.axeYAide?.[actLanguage]} />
                    </Box>
                  </Box>
                  {/* Y Axis */}
                  <Box sx={{ width: "30px", flexShrink: 0, position: "relative", height: CHART_H+"px" }}>
                    {ticks.map((t) => (
                      <span
                        key={t}
                        style={{
                          position: "absolute",
                          right: 4,
                          bottom: `${(t / MAX_VAL) * 100}%`,
                          fontSize: "11px",
                          color: "#999",
                          transform: "translateY(50%)",
                          lineHeight: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t}%
                      </span>
                    ))}
                  </Box>

                  {/* Plot */}
                  <Box sx={{ flex: 1, position: "relative", height: CHART_H+"px" }}>
                    {/* Grid lines */}
                    <svg
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {ticks.map((t) => (
                        <line
                          key={t}
                          x1="0" x2="100%"
                          y1={`${100 - (t / MAX_VAL) * 100}%`}
                          y2={`${100 - (t / MAX_VAL) * 100}%`}
                          stroke="currentColor"
                          strokeOpacity="0.15"
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </svg>

                    {/* Bars */}
                    <Box sx={{ display: "flex", alignItems: "flex-end", height: "100%", position: "relative", zIndex: 1 }}>
                      {data.map((d, i) => {
                        // Point 2 de la review du 04/08 : les deux états étaient
                        // empilés, l'« après » dessiné DERRIÈRE l'« avant ». Seul
                        // l'écart dépassait, donc invisible quand la progression
                        // était faible, et totalement caché quand elle était
                        // négative. On dessine désormais deux barres côte à côte,
                        // chacune à sa vraie hauteur, avec ses propres couleurs.
                        // D12 review 02/09 : percentile de la tranche = part des
                        // apprenants situes en dessous d'elle (bornes cumulees)
                        const totalEleves = data.reduce((acc, x) => acc + (x?.nbrStudents || 0), 0)
                        const sousTranche = data.slice(0, i).reduce((acc, x) => acc + (x?.nbrStudents || 0), 0)
                        const percentile = totalEleves > 0 ? Math.max(1, Math.round((sousTranche / totalEleves) * 100)) : 0
                        const texteAvant = (languageValues?.adminDashboard?.mappingPage?.graph?.tooltipAvant?.[actLanguage] || "")
                          .replace("{score}", parseInt(d.moyStartScore)).replace("{percentile}", percentile)
                        const texteApres = (languageValues?.adminDashboard?.mappingPage?.graph?.tooltipApres?.[actLanguage] || "")
                          .replace("{score}", parseInt(d.moyEndScore)).replace("{percentile}", percentile)
                        const hauteurAvant = anime ? Math.max(2, (d.moyStartScore / MAX_VAL) * CHART_H) : 2
                        const hauteurApres = anime ? Math.max(2, (d.moyEndScore / MAX_VAL) * CHART_H) : 2
                        const totalPx = Math.max(hauteurAvant, hauteurApres)
                        const enHausse = d.moyEndScore >= d.moyStartScore


                        return (
                          <Box
                            key={i}
                            sx={{ width: widths[i]+"%", display: "flex", opacity:d?.nbrStudents>0?1:0, flexDirection: "column", alignItems: "center"}}
                          >
                            {/* Badge + bar stack */}
                            <Box sx={{ width: "calc(100% - 20px)", position: "relative", height: (totalPx + BADGE_H)+"px", transition: "height 0.8s ease" }}>
                              {/* Delta badge */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  background: "#fff",
                                  border: "1.5px solid #222",
                                  borderRadius: "20px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  padding: "3px 6px",
                                  whiteSpace: "nowrap",
                                  color: "#222",
                                  zIndex: 2,
                                }}
                              >
                                {d.progression >= 0 && "+"}
                                {Math.round(d.progression * 100) / 100}
                                %
                              </Box>

                              {/* Deux barres jumelles : avant / après */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: totalPx+"px",
                                  transition: "height 0.8s ease",
                                  display: "flex",
                                  alignItems: "flex-end",
                                  justifyContent: "center",
                                  gap: "6px",
                                }}
                              >
                                {/* AVANT — D12 : bulle explicative au survol */}
                                <Tooltip title={texteAvant} arrow placement="top">
                                <Box
                                  id={"anchorTooltipBefore_"+i}
                                  sx={{
                                    width: "44%",
                                    height: hauteurAvant+"px",
                                    transition: "height 0.8s ease",
                                    background: "#F5C4B3",
                                    borderRadius: "8px 8px 0 0",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    paddingTop: "4px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#545455",
                                  }}
                                >
                                  {parseInt(d.moyStartScore)}%
                                </Box>
                                </Tooltip>
                                {/* APRÈS — D12 : bulle explicative au survol */}
                                <Tooltip title={texteApres} arrow placement="top">
                                <Box
                                  id={"anchorTooltipAfter_"+i}
                                  sx={{
                                    width: "44%",
                                    height: hauteurApres+"px",
                                    transition: "height 0.8s ease",
                                    background: enHausse ? "#D85A30" : "#9A9A9A",
                                    borderRadius: "8px 8px 0 0",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    paddingTop: "4px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "#fff",
                                  }}
                                >
                                  {parseInt(d.moyEndScore)}%
                                </Box>
                                </Tooltip>
                              </Box>
                              {/* D10, relecture 02/09 : le losange « Positionnement »
                                  dessine ici placait un RANG (0 a 100) sur l'axe des
                                  SCORES, dont l'echelle s'adapte aux donnees : au-dela
                                  du percentile 75 il sortait du cadre. Deux unites
                                  differentes, donc un repere qui ne veut rien dire.
                                  Retire en attendant que Nawal precise ce que trace
                                  « Positionnement » sur le Figma. La legende et les
                                  bulles au survol, elles, restent : leurs textes
                                  viennent d'elle. */}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Expected level */}

                    {viewExpectedLevel &&
                      <Box sx={{
                        position:"absolute",
                        bottom:"calc("+(expectedLevel / maxTick)*100+"% - 1px)",
                        width:"100%",
                        borderBottom:"2px dashed rgba(0,0,0,0.5)",
                        paddingBottom:"2px",
                        fontSize:"12px",
                        color:"black",
                      }}>
                        {languageValues?.adminDashboard?.mappingPage?.graph?.expectedLevel[actLanguage]} {expectedLevel}%
                      </Box>
                    }

                  </Box>
                </Box>


                {/* X labels */}
                <Box sx={{ display: "flex", marginLeft: DECALAGE_TRACE+"px" }}>
                  {data.map((d, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: `${widths[i]}%`,
                        flexShrink: 0,
                        textAlign: "center",
                        marginTop: "7px",
                        fontSize: "12px",
                        color: "#888",
                      }}
                    >
                      {d.label}
                    </Box>
                  ))}
                </Box>

                {/* P1 : ce que porte l'axe horizontal, et ce qu'est un percentile */}
                <Box sx={{
                  marginLeft: DECALAGE_TRACE+"px", marginTop: "10px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11px", color: "#888", textAlign: "center",
                }}>
                  {languageValues?.adminDashboard?.mappingPage?.graph?.axeX?.[actLanguage]}
                  <PastilleAide texte={languageValues?.adminDashboard?.mappingPage?.graph?.axeXAide?.[actLanguage]} />
                </Box>

              </Box>

          ) : (
            <Box sx={{minHeight:"177px", display:"flex", justifyContent:"center", alignItems:"center"}}>
              <CircularProgress size="50px" sx={{'color': 'black'}} />
            </Box>
          )}
        </Box>

        {/* D10 — repere « Positionnement », finalise le 03/09 sur la reponse
            d'Hicham : c'est un PERCENTILE, donc un rang dans la promotion, et non
            un score. Il ne pouvait pas se dessiner sur l'axe des scores, dont
            l'echelle s'adapte aux donnees : au-dela du percentile 75 le losange
            sortait du cadre. Il a donc sa propre regle, de 0 a 100 fixe.

            ⚠️ Cette regle vit VOLONTAIREMENT hors du conteneur a defilement
            horizontal ci-dessus : celui-ci porte `overflowY: hidden`, qui la
            rognait entierement. Elle etait dans le DOM, mesurable, et invisible
            a l'ecran — c'est la capture au navigateur qui l'a montre, pas la
            sonde. Ne pas la remettre a l'interieur. */}
        {data?.length > 0 && <ReglePositionnement data={data} />}
      </Box>

      {canViewTooltips !== false &&
        <>
          <Popover
            open={beforePopover}
            anchorEl={document.getElementById("anchorTooltipBefore_"+canViewTooltips)}
            onClose={() => { setBeforePopover(false) }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{padding:"10px", width:"170px"}}>
              <Box sx={{display:"flex", justifyContent:"flex-end"}}>
                <img src={chatInfoIcon} style={{width:"15px", height:"15px"}} />
              </Box>
              <Box sx={{padding:"0px 10px 10px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:"10px"}}>
                <Box sx={{width:"85px", height:"100px", borderRadius:"10px", border:"1px solid grey", position:"relative"}}>
                  <Box sx={{
                    position:"absolute",
                    bottom:0,
                    width:"100%",
                    height:parseInt(data[canViewTooltips]?.moyStartScore)+"%",
                    borderRadius:"9px",
                    background:"#F3CCBD",
                    color:"#545455",
                    fontSize:"14px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                  }}>
                    {parseInt(data[canViewTooltips]?.moyStartScore)}%
                  </Box>
                </Box>
                <Box sx={{textAlign:"center", color:"text.black", fontSize:"15px", lineHeight:"125%", whiteSpace:"pre-line"}}>
                  {languageValues?.adminDashboard?.mappingPage?.graph?.knowledgeBeforeLegend[actLanguage]}
                </Box>
                <Box sx={{textAlign:"center", color:"text.subTitle", fontSize:"12px"}}>
                  {languageValues?.adminDashboard?.mappingPage?.graph?.legendSubTitle[actLanguage]}
                </Box>
              </Box>
            </Box>
          </Popover>

          <Popover
            open={afterPopover}
            anchorEl={document.getElementById("anchorTooltipAfter_"+canViewTooltips)}
            onClose={() => { setAfterPopover(false) }}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{padding:"10px", width:"170px"}}>
              <Box sx={{display:"flex", justifyContent:"flex-end"}}>
                <img src={chatInfoIcon} style={{width:"15px", height:"15px"}} />
              </Box>
              <Box sx={{padding:"0px 10px 10px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:"10px"}}>
                <Box sx={{width:"85px", height:"100px", borderRadius:"10px", border:"1px solid grey", position:"relative"}}>
                  <Box sx={{
                    position:"absolute",
                    bottom:0,
                    width:"100%",
                    height:parseInt(data[canViewTooltips]?.moyEndScore)+"%",
                    borderRadius:"9px",
                    background:"#E26438",
                    color:"white",
                    fontSize:"14px",
                    display:"flex",
                    alignItems:"center",
                    justifyContent:"center",
                  }}>
                    {parseInt(data[canViewTooltips]?.moyEndScore)}%
                  </Box>
                </Box>
                <Box sx={{textAlign:"center", color:"text.black", fontSize:"15px", lineHeight:"125%", whiteSpace:"pre-line"}}>
                  {languageValues?.adminDashboard?.mappingPage?.graph?.knowledgeAfterLegend[actLanguage]}
                </Box>
                <Box sx={{textAlign:"center", color:"text.subTitle", fontSize:"12px"}}>
                  {languageValues?.adminDashboard?.mappingPage?.graph?.legendSubTitle[actLanguage]}
                </Box>
              </Box>
            </Box>
          </Popover>
        </>
      }
    </>
  );
}

export default ProgressionChart
